import { LeftOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Upload } from 'antd'
import { UploadFile } from 'antd/lib'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useQuestionnairesServiceGetQuestionnaireAnswerEndpoint,
  useQuestionnairesServiceGetQuestionnaireEndpoint
} from 'src/api/queries'
import { MediaFileResponse3 } from 'src/api/requests'
import {
  CustomItemRender,
  LoadingScreen,
  PageBreadcrumbs,
  Text
} from 'src/components'
import { mapAnswersToQuizDetails } from 'src/features/persons/components/PersonQuiz'
import { QuizDetailsForm } from 'src/features/persons/components/PersonQuiz/components/QuizDetailsForm'
import { useStyles } from 'src/features/persons/components/PersonQuiz/styles'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { onDownloadFile } from 'src/hooks'
import { useLocales } from 'src/locales'

type Document = UploadFile & { id?: string }

export const QuizDetails = () => {
  const navigate = useNavigate()
  const { id, personId } = useParams()
  const { t } = useLocales()

  const { styles } = useStyles()

  const { data: quizDetails, isLoading: isQuizLoading } =
    useQuestionnairesServiceGetQuestionnaireEndpoint({
      id: String(id)
    })

  const { data: quizAnswers, isLoading } =
    useQuestionnairesServiceGetQuestionnaireAnswerEndpoint({
      id: String(id),
      personId: String(personId)
    })

  const { breadcrumbItems, fullName, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const onBackClick = () => {
    navigate('..', { relative: 'path' })
  }

  const mappedQuestions = mapAnswersToQuizDetails(
    quizDetails,
    quizAnswers?.questions
  )

  if (isLoading || isLoadingPerson || isQuizLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs title={fullName} items={breadcrumbItems}>
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>
        </Flex>

        {!quizAnswers ? (
          <Card>
            <Text weight={700}>
              {t('questionnaires.quiz_has_no_answers')}
            </Text>{' '}
          </Card>
        ) : (
          <Card>
            <Flex vertical gap={16}>
              <Flex vertical className={styles.questionAnswerWrapper} gap={48}>
                <QuizDetailsForm data={mappedQuestions} />
              </Flex>

              <Upload
                itemRender={(_, file: Document) => (
                  <CustomItemRender
                    file={file}
                    onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                  />
                )}
                fileList={quizAnswers?.mediaFiles?.map(
                  (file: MediaFileResponse3, index: number) => ({
                    ...file,
                    uid: String(file?.id ?? index),
                    name: file?.name ?? `file-${index}`
                  })
                )}
                disabled
              />
            </Flex>
          </Card>
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
